import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiCalendar, FiShare2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { insights } from '../data/insights';

const InsightDetail = () => {
  const { slug } = useParams();
  const article = insights.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Article Not Found</h2>
        <Link to="/insights" className="text-cyan-600 hover:underline">Back to Insights</Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 relative bg-white min-h-screen">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.05] pointer-events-none"></div>
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link 
            to="/insights" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-600 transition-colors font-bold uppercase tracking-widest text-[10px]"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Insights
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 bg-cyan-50 px-3 py-1 border border-cyan-200">
              {article.tag}
            </span>
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
              <SafeIcon icon={FiCalendar} />
              {article.date || 'April 2026'}
            </div>
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
              <SafeIcon icon={FiClock} />
              {article.readTime}
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-8 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 mb-12 pb-12 border-b border-slate-100">
             <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white text-[10px] font-bold">
               AL
             </div>
             <div>
               <div className="text-sm font-bold text-slate-900">Aetrix Logic Editorial</div>
               <div className="text-xs text-slate-500 uppercase tracking-widest">Engineering & Strategy</div>
             </div>
             <button className="ml-auto w-10 h-10 flex items-center justify-center border border-slate-200 text-slate-400 hover:text-cyan-600 hover:border-cyan-600 transition-all">
               <SafeIcon icon={FiShare2} />
             </button>
          </div>

          <div 
            className="prose prose-slate prose-lg max-w-none 
              prose-headings:text-slate-900 prose-headings:font-bold 
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-strong:text-slate-900 prose-blockquote:border-cyan-500
              prose-blockquote:bg-slate-50 prose-blockquote:p-8
              prose-li:text-slate-600"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 p-12 bg-slate-900 relative overflow-hidden text-center"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl pointer-events-none"></div>
          <h2 className="text-2xl font-bold text-white mb-4 relative z-10">Need a system that works as well as you do?</h2>
          <p className="text-slate-400 mb-8 relative z-10">Let's build an intelligent platform tailored to your specific operations.</p>
          <Link 
            to="/book-call"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white font-black uppercase tracking-widest text-[10px] hover:bg-cyan-400 transition-all relative z-10"
          >
            Book a Discovery Call
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default InsightDetail;
